import { useEffect, useState } from "react";
import { ShieldAlert, UserMinus } from "lucide-react";
import { getContactRemoveCheck, removePrimeContact, type ContactRemoveCheck, type RemoveContactResult } from "../api/CrmApi";
import { apiErrorMessage } from "../apiError";
import { formatDate } from "./crmUi";
import ConfirmModal from "shared/ConfirmModal";
import Modal from "shared/Modal";
import Button from "shared/Button";
import Alert from "shared/Alert";

interface Props {
  contactId: number;
  onClose: () => void;
  onRemoved: (result: RemoveContactResult) => void;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const STATUS_LABELS: Record<string, string> = { do_not_contact: "Do Not Contact", bounced: "Bounced" };

/**
 * Remove a contact person. The server says up front whether the no-repeat-contact rules forbid it (marked Do Not Contact /
 * Bounced, or the company's last record on a campaign that still locks it): then this shows why and offers no remove
 * button. Otherwise it is a confirmation that states exactly what goes, replies and flags in emphasis.
 */
export default function RemoveContactModal({ contactId, onClose, onRemoved }: Props) {
  const [check, setCheck] = useState<ContactRemoveCheck | null>(null);
  const [loadError, setLoadError] = useState("");
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    getContactRemoveCheck(contactId)
      .then((c) => !cancelled && setCheck(c))
      .catch((e) => !cancelled && setLoadError(apiErrorMessage(e, "Could not check whether this contact can be removed.")));
    return () => { cancelled = true; };
  }, [contactId]);

  async function confirmRemove() {
    setRemoving(true);
    setError("");
    try {
      onRemoved(await removePrimeContact(contactId));
    } catch (err: any) {
      setError(apiErrorMessage(err, "Could not remove the contact. Please try again."));
      setRemoving(false);
    }
  }

  if (!check) {
    return (
      <Modal
        size="md"
        title="Remove contact"
        icon={<UserMinus />}
        onClick={onClose}
        closeButtonCLick={onClose}
        footer={<Button variant="outline" size="sm" onClick={onClose}>Close</Button>}
      >
        {loadError ? <Alert tone="danger" role="alert">{loadError}</Alert> : (
          <div className="flex items-center justify-center py-8" data-testid="remove-loading">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </Modal>
    );
  }

  if (check.blocked) {
    return (
      <Modal
        size="md"
        title={`${check.name} cannot be removed yet`}
        description="The no-repeat-contact rules still rely on this contact"
        icon={<ShieldAlert />}
        onClick={onClose}
        closeButtonCLick={onClose}
        footer={<Button variant="outline" size="sm" onClick={onClose}>Close</Button>}
      >
        <ul className="space-y-3 text-sm text-foreground" data-testid="remove-blocked">
          {check.flagged && (
            <li>
              Marked <span className="font-medium">{STATUS_LABELS[check.flagged.status] ?? check.flagged.status}</span>
              {check.flagged.reason ? <> ({check.flagged.reason})</> : null}. The mark is what stops outreach to this person, and a later
              sheet that lists the address would add them back as an active contact. Set them back to active on their page first if you
              really want them gone.
            </li>
          )}
          {check.campaigns.map((c) => (
            <li key={c.opportunity_id}>
              The company's only record on <span className="font-medium">{c.title}</span>, a campaign that still locks the company until{" "}
              <span className="font-medium">{formatDate(c.locked_until, true)}</span> (the campaign's last touch plus the 7-day cooldown).
              Removing them now would let another campaign contact the company again.
            </li>
          ))}
        </ul>
      </Modal>
    );
  }

  const { impact } = check;
  const more = impact.rfp_count - impact.rfps.length;
  return (
    <ConfirmModal
      title={`Remove ${check.name}?`}
      variant="danger"
      confirmLabel="Remove contact"
      confirming={removing}
      onCancel={onClose}
      onConfirm={confirmRemove}
      message={
        <div className="space-y-3" data-testid="remove-message">
          <p>
            This permanently removes <span className="font-medium text-foreground">{check.name}</span> ({check.email})
            {check.company_name ? <> from {check.company_name}</> : null}
            {impact.rfp_contact_rows > 0 ? <> and deletes {plural(impact.rfp_contact_rows, "RFP record")}</> : null}. The company and its other
            contacts stay. It cannot be undone.
          </p>
          {impact.rfps.length > 0 && (
            <p className="text-xs">
              On: {impact.rfps.map((r) => r.title || `RFP #${r.id}`).join("; ")}
              {more > 0 ? ` and ${plural(more, "other RFP")}` : ""}.
            </p>
          )}
          {impact.replies > 0 && (
            <Alert tone="danger" role="alert">
              {plural(impact.replies, "RFP record")} hold{impact.replies === 1 ? "s" : ""} a reply or conversation with this person. They are
              deleted with the contact and cannot be recovered.
            </Alert>
          )}
          {impact.high_value > 0 && (
            <Alert tone="warning">
              Marked high value on {plural(impact.high_value, "record")}. The flag and its note are deleted, and will no longer carry forward
              to future sheets.
            </Alert>
          )}
          {impact.was_primary && (
            <Alert tone="warning">
              This is the company's primary contact. {check.company_name || "The company"} will have no primary contact until you choose one.
            </Alert>
          )}
          <p className="text-xs">
            Removing is not a block: if a later primes sheet lists this address, the person is created again as a new, empty contact. To stop
            outreach to someone, mark them Do Not Contact instead.
          </p>
          {error && <Alert tone="danger" role="alert">{error}</Alert>}
        </div>
      }
    />
  );
}
